import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Check, X, Loader2 } from 'lucide-react';
import { API } from '../../Common/Api';
import { SuccessToast, ErrorToast } from '../../Utils/toast';
import { path } from '../../Utils/ScreenPath';
import login from '../../assets/login.png';

const validatePassword = (password) => ({
  length: password.length >= 8,
  uppercase: /[A-Z]/.test(password),
  lowercase: /[a-z]/.test(password),
  number: /\d/.test(password),
  special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
});

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  // status: verifying | valid | invalid | success
  const [status, setStatus] = useState('verifying');
  const [accountName, setAccountName] = useState('');
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [show, setShow] = useState({ newPassword: false, confirmPassword: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Verify the token on mount
  useEffect(() => {
    let active = true;
    const verify = async () => {
      if (!token) {
        setStatus('invalid');
        return;
      }
      try {
        const res = await API.VerifyResetToken(JSON.stringify({ token }));
        if (!active) return;
        if (res?.success && res?.data?.valid) {
          setAccountName(res.data.username || res.data.email || '');
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      } catch (e) {
        if (active) setStatus('invalid');
      }
    };
    verify();
    return () => { active = false; };
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const requirements = validatePassword(form.newPassword);

  const handleSubmit = async () => {
    const newErrors = {};
    if (!form.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (!Object.values(requirements).every(Boolean)) {
      newErrors.newPassword = 'Password does not meet requirements';
    }
    if (!form.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (form.newPassword !== form.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      const res = await API.SetPasswordWithToken(JSON.stringify({
        token,
        newPassword: form.newPassword,
        confirmPassword: form.confirmPassword,
      }));
      if (res?.success) {
        SuccessToast(res.message || 'Password set successfully. Please log in.');
        setStatus('success');
        setTimeout(() => navigate(path.login), 1500);
      } else {
        ErrorToast(res?.message || 'Failed to set password.');
        // Token may have expired between verify and submit
        if (res?.message && /expired|invalid/i.test(res.message)) setStatus('invalid');
      }
    } catch (e) {
      ErrorToast('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Requirement = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-xs">
      {ok ? <Check className="w-3.5 h-3.5 text-green-500" /> : <X className="w-3.5 h-3.5 text-gray-300" />}
      <span className={ok ? 'text-green-600' : 'text-gray-400'}>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6">
      <div className="w-full max-w-md bg-white rounded-2xl p-6 sm:p-8 shadow-xl">
        <div className="w-16 h-16 mb-4">
          <img src={login} alt="OmniCard" />
        </div>

        {status === 'verifying' && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Loader2 className="w-8 h-8 text-[#E2231A] animate-spin mb-3" />
            <p className="text-sm text-gray-600">Verifying your link…</p>
          </div>
        )}

        {status === 'invalid' && (
          <div className="text-center py-6">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <X className="w-6 h-6 text-[#E2231A]" />
            </div>
            <h1 className="text-[20px] font-bold text-[#1F1715] mb-2">Link invalid or expired</h1>
            <p className="text-sm text-gray-500 mb-6">
              This password link is no longer valid. Password links expire after 48 hours and can be used once.
              Please request a new one from the login page.
            </p>
            <button
              onClick={() => navigate(path.login)}
              className="bg-[#E2231A] hover:bg-[#c41d15] text-white text-sm px-6 py-2.5 rounded-full font-medium"
            >
              Go to Login
            </button>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center py-6">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-[20px] font-bold text-[#1F1715] mb-2">Password set!</h1>
            <p className="text-sm text-gray-500">Redirecting you to login…</p>
          </div>
        )}

        {status === 'valid' && (
          <>
            <h1 className="text-[20px] font-bold text-[#1F1715] mb-1">Set your password</h1>
            <p className="text-sm text-gray-500 mb-6">
              {accountName ? <>For <span className="font-medium text-gray-700">{accountName}</span></> : 'Create a password for your account'}
            </p>

            {/* New Password */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#1F1715] mb-2">New Password</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Lock className="w-5 h-5 text-slate-400" />
                </div>
                <input
                  type={show.newPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={form.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  className={`w-full pl-10 pr-10 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E2231A] ${errors.newPassword ? 'border-red-400' : 'border-gray-300'}`}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => ({ ...s, newPassword: !s.newPassword }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {show.newPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.newPassword && <p className="text-xs text-red-500 mt-1">{errors.newPassword}</p>}
            </div>

            {/* Requirements */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-4 px-1">
              <Requirement ok={requirements.length} label="At least 8 characters" />
              <Requirement ok={requirements.uppercase} label="One uppercase letter" />
              <Requirement ok={requirements.lowercase} label="One lowercase letter" />
              <Requirement ok={requirements.number} label="One number" />
              <Requirement ok={requirements.special} label="One special character" />
            </div>

            {/* Confirm Password */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1F1715] mb-2">Confirm Password</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Lock className="w-5 h-5 text-slate-400" />
                </div>
                <input
                  type={show.confirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                  placeholder="Re-enter new password"
                  className={`w-full pl-10 pr-10 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E2231A] ${errors.confirmPassword ? 'border-red-400' : 'border-gray-300'}`}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => ({ ...s, confirmPassword: !s.confirmPassword }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {show.confirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`w-full text-white text-sm py-2.5 rounded-full font-medium ${submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#E2231A] hover:bg-[#c41d15]'}`}
            >
              {submitting ? 'Setting password…' : 'Set Password'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
